import React, { PureComponent } from 'react';
import { PieChart, Pie, Sector, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';
import { getPrettyMonth } from './utility';

const MainContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 16px;

  @media screen and (max-width: 900px) {
    flex-direction: column;
    .content {
      width: 100%;
    }
  }

  .content {
    width: 70%;
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    justify-content: center;
    background-color: #fff;
    height: auto;

    @media screen and (max-width: 900px) {
      width: 100%;
    }

    ul {
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 22px;
      width: 100%;

      li {
        display: flex;
        align-items: center;
        gap: 8px;
        span {
          font-family: 'neueMontreal-Regular';
          font-size: 16px;
          font-weight: 500;
          line-height: 14.4px;
          text-align: left;
          color: #151b2e;
        }
      }
    }
  }
`;

type Payload = {
  name: string;
  label: string;
  value: number;
  fill: string;
  date: string;
};

type RenderActiveShapeProps = {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
  payload: Payload;
  percent: number;
  value: number;
};

const renderActiveShape = (props: RenderActiveShapeProps) => {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
  } = props;

  const expandedOuterRadius = outerRadius + 3;
  const shadowColor = 'rgba(0.1, 0, 0, 0)';

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={expandedOuterRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        stroke={shadowColor}
        strokeWidth={4}
        style={{ filter: `drop-shadow(0px 1px 0px ${shadowColor})` }}
      />
      <text
        x={cx}
        y={cy + 10}
        dy={8}
        textAnchor="middle"
        fontSize={14}
        fill="#000"
      >
        {payload.label}
      </text>
      <text
        x={cx}
        y={cy - 10}
        fontSize={24}
        fontWeight={600}
        textAnchor="middle"
        fill="#333"
      >
        {`${payload.value.toFixed(2)}%`}
      </text>
      <text
        x={cx}
        y={cy + 30}
        dy={8}
        textAnchor="middle"
        fontSize={14}
        fill="#ABABAB"
      >
        {payload.date}
      </text>
    </g>
  );
};

interface StockPieChartProps {
  activeData: Payload;
  onPieClick: (payload: Payload) => void;
  shareHoldingData: {
    yearlyRecordCount(yearlyRecordCount: string): string;
    DIIMFPer?: number;
    FIIPer?: number;
    insurancePer?: number;
    mutualFundsPer?: number;
    othersPer: number;
    promotersPer?: number;
  };
}

interface StockPieChartState {
  activeIndex: number;
}

export class StockPieChart extends PureComponent<
  StockPieChartProps,
  StockPieChartState
> {
  state = {
    activeIndex: 0,
  };

  // TODO: Replace `any` with specific type
  /* eslint-disable @typescript-eslint/no-explicit-any */
  onPieEnter = (_: any, index: number) => {
    this.setState({
      activeIndex: index,
    });
    const activeEntry = this.getChartData()[index];
    this.props.onPieClick(activeEntry);
  };

  getChartData() {
    const { shareHoldingData } = this.props;
    console.log(shareHoldingData, 'shareHoldingData');

    return [
      {
        name: 'promotersPer',
        legendLabel: 'Promoters',
        label: 'Promoters',
        value: shareHoldingData?.promotersPer ?? 0,
        fill: '#3F7ED3',
      },
      {
        name: 'DIIMFPer',
        legendLabel: 'DII & MF',
        label: 'DII & MF',
        value: shareHoldingData?.DIIMFPer ?? 0,
        fill: '#FFC502',
      },
      {
        name: 'mutualFundsPer',
        legendLabel: 'Mutual Funds',
        label: 'Mutual Funds',
        value: shareHoldingData?.mutualFundsPer ?? 0,
        fill: '#FB7485',
      },
      {
        name: 'FIIPer',
        legendLabel: 'FII',
        label: 'Foreign Institutions - FII',
        value: shareHoldingData?.FIIPer ?? 0,
        fill: '#A4A5F2',
      },
      {
        name: 'insurancePer',
        legendLabel: 'Insuarance Companies',
        label: 'Insurance Companies',
        value: shareHoldingData?.insurancePer ?? 0,
        fill: '#BB5A88',
      },
      {
        name: 'othersPer',
        legendLabel: 'Others',
        label: 'Others',
        value: shareHoldingData?.othersPer ?? 0,
        fill: '#F26344',
      },
    ].map((v) => ({
      ...v,
      date: getPrettyMonth(shareHoldingData.yearlyRecordCount),
    }));
  }
  render() {
    const { activeIndex } = this.state;
    const chartData = this.getChartData();

    return (
      <MainContainer>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              activeIndex={activeIndex}
              activeShape={renderActiveShape as (props: unknown) => JSX.Element}
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={80}
              outerRadius={105}
              dataKey="value"
              onMouseEnter={this.onPieEnter}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="content">
          <ul>
            {chartData.map((entry, i) => (
              <li key={i}>
                <div
                  style={{
                    backgroundColor: entry?.fill,
                    width: '18px',
                    height: '18px',
                    borderRadius: '4px',
                    flexShrink: 0,
                  }}
                />
                <span>{entry?.legendLabel}</span>
                <span>({parseFloat(entry?.value.toString()).toFixed(2)}%)</span>
              </li>
            ))}
          </ul>
        </div>
      </MainContainer>
    );
  }
}
